import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { FeedbackType } from "components/common/FeedbackButtons"
import { PROBLEM_ASSESSMENT_TYPE_MAPPING } from "utils/external/swayam/formativeAssessment"

interface SubjectiveAssessmentInitialStates {
  stage: string
  currentTestSessionDetails: any
  submitSubjectiveAssessmentData: any
  subjectiveAssessmentResult: any
  auditLogsHeartbeatStarted: boolean
  auditLogsCompleteData: any
}

const initialState: SubjectiveAssessmentInitialStates = {
  stage: PROBLEM_ASSESSMENT_TYPE_MAPPING[0],
  currentTestSessionDetails: null,
  submitSubjectiveAssessmentData: null,
  subjectiveAssessmentResult: null,
  auditLogsHeartbeatStarted: false,
  auditLogsCompleteData: {
    video_url: "video_na",
    widget_code: "comprehensive_written_evaluation",
    action_performed: null,
    payload: {
      seg_id: "segment_no",
      input_params_obj: {
        topic: "topic_na",
        difficulty_level: "hard",
        assessment_type: "course_specific",
        rubric_criteria: [
          "Articulation",
          "Vocabulary",
          "Sentence Structure",
          "Command on Language",
          "Clarity of Thoughts",
        ],
        consider_history: false,
        custom_instructions: "",
        question_count: 1,
        time_in_sec: 0,
      },
      assessment_obj: {
        topic: "topic_na",
        time_spent_seconds: 0,
        questions_attempted: 0,
        questions_total: 0,
        overall_ai_feedback_text: "overall_ai_feedback_text_na",
        score: { ques_na: 0 },
        ques_details: [
          {
            ques_id: "ques_id_na",
            ques_text: "ques_text_na",
            ques_type: "ques_type_no",
            ques_blooms_taxonomy: "ques_blooms_taxonomy_na",
            ques_difficulty_level: "ques_difficulty_level_na",
            ans_id: "ans_id_na",
            ans_text: "ans_text_na",
            ai_feedback_text: "ai_feedback_text_na",
            tokens_used: 0,
            time_spent_seconds: 0,
            rubric_score: ["rubric_score_na"],
            overall_score: 0,
          },
        ],
        completion_status: "partial",
        areas_of_improvement: ["areas_of_improvement_na"],
        areas_of_strengths: ["areas_of_strengths_na"],
        rubric_score: ["rubric_score_na"],
        overall_score: 0,
      },
      blob_uri: {
        complete_blob_uri: [],
        generated_blob_uri: [],
        uploaded_blob_uri: [],
        downloaded_blob_uri: [],
      },
      compute_time: 0,
      tokens_used: 0,
      cache_hit: true,
      session_timeout: false,
      usage_time_in_sec: 0,
      watch_percentage: 0,
      video_duration: 0,
    },
  },
}

const SubjectiveAssessmentSlice = createSlice({
  name: "subjective-assessment",
  initialState,
  reducers: {
    setStage: (state, action: PayloadAction<string>) => {
      state.stage = action.payload
    },
    setCurrentTestSessionDetails: (state, action: PayloadAction<any>) => {
      state.currentTestSessionDetails = action.payload
    },
    setSubmitSubjectiveAssessmentData: (state, action: PayloadAction<any>) => {
      state.submitSubjectiveAssessmentData = action.payload
    },
    setSubjectiveAssessmentResult: (state, action: PayloadAction<any>) => {
      state.subjectiveAssessmentResult = action.payload
    },
    setAuditLogsHeartbeatStarted: (state, action: PayloadAction<boolean>) => {
      state.auditLogsHeartbeatStarted = action.payload
    },
    setAuditLogsCompleteData: (state, action: PayloadAction<any>) => {
      state.auditLogsCompleteData = action.payload
    },
    setSubjectiveAssessmentFeedbackRating: (state, action: PayloadAction<FeedbackType>) => {
      if (state.subjectiveAssessmentResult) {
        state.subjectiveAssessmentResult.feedbackRating = action.payload
      }
    },
    resetState: () => initialState,
  },
})

export const {
  setStage,
  setCurrentTestSessionDetails,
  setSubmitSubjectiveAssessmentData,
  setSubjectiveAssessmentResult,
  setAuditLogsHeartbeatStarted,
  setAuditLogsCompleteData,
  setSubjectiveAssessmentFeedbackRating,
  resetState,
} = SubjectiveAssessmentSlice.actions
export default SubjectiveAssessmentSlice.reducer
